import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Field, Modal } from "@fawkes/ui";

/**
 * Twee relaties tot één maken.
 *
 * <p><b>Waarom dit er is.</b> Dubbele relaties ontstaan ook met een waarschuwing erbij: twee
 * medewerkers tegelijk, een import, een naam die net anders geschreven is. Zonder samenvoegen blijft
 * zo'n dubbel staan en raakt het dossier van die persoon verdeeld over twee plekken — en wie de ene
 * opent ziet niet dat er een andere is.
 *
 * <p><b>De relatie waar je op staat verdwijnt</b>, en gaat op in de relatie die je hier kiest. Dat
 * staat er met zoveel woorden bij, want andersom zou je je eigen zoekwerk weggooien. Wat er precies
 * meegaat — dieren, facturen, verblijven, notities, documenten — is wat de server erover zegt, en
 * dat laat hij na afloop zien.
 *
 * <p><b>Niet terug te draaien met een knop.</b> Daarom twee stappen: eerst kiezen, dan bevestigen
 * met de naam van de relatie die blijft in beeld.
 */

interface Kandidaat {
  id: number;
  klantNummer: number | null;
  klantnaam: string | null;
  woonplaats: string | null;
  emailAdres: string | null;
}

export function RelatieSamenvoegDialog({ bronId, bronNaam, onClose, onKlaar }: {
  /** De relatie die verdwijnt: degene waar je op staat. */
  bronId: string;
  bronNaam: string;
  onClose: () => void;
  /** Naar de relatie die alles heeft overgenomen. */
  onKlaar: (doelId: number) => void;
}) {
  const t = useTranslate();
  const [zoek, setZoek] = useState("");
  const [gekozen, setGekozen] = useState<Kandidaat | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const { mutate, isLoading: bezig } = useCustomMutation();

  // Dezelfde gepagineerde lijst als het relatieoverzicht; een eigen zoek-endpoint zou een tweede
  // manier van zoeken zijn die anders uitpakt dan de lijst waar iedereen aan gewend is.
  const { data } = useCustom<{ rows: Kandidaat[]; total: number }>({
    url: "relaties",
    method: "get",
    config: { query: { q: zoek.trim(), page: 1, size: 10 } },
    queryOptions: { enabled: zoek.trim().length >= 2 },
  });
  // De relatie waar je op staat kan niet in zichzelf opgaan.
  const rijen = (data?.data?.rows ?? []).filter((r) => String(r.id) !== String(bronId));

  const samenvoegen = () => {
    if (!gekozen) return;
    setFout(null);
    mutate(
      {
        url: `relaties/${bronId}/samenvoegen`,
        method: "post",
        values: { doelId: gekozen.id },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => onKlaar(gekozen.id),
        onError: () => setFout(t("relatieSamenvoegen.fout")),
      },
    );
  };

  return (
    <Modal
      open
      title={t("relatieSamenvoegen.title")}
      onClose={onClose}
      footer={
        <>
          {fout && <span className="fawkes-error" role="alert">{fout}</span>}
          <Button variant="secondary" onClick={onClose} disabled={bezig}>
            {t("relatieSamenvoegen.annuleer")}
          </Button>
          <Button variant="primary" onClick={samenvoegen} disabled={!gekozen || bezig}>
            {bezig ? t("relatieSamenvoegen.bezig") : t("relatieSamenvoegen.bevestig")}
          </Button>
        </>
      }
    >
      <div className="fawkes-samenvoeg">
        <p className="fawkes-samenvoeg__uitleg">{t("relatieSamenvoegen.uitleg")}</p>

        <Field
          label={t("relatieSamenvoegen.zoek")}
          value={zoek}
          onChange={(e) => { setZoek(e.target.value); setGekozen(null); }}
        />

        {gekozen ? (
          /* Wat er gaat gebeuren in één zin, met beide namen erin: dit is het moment om te zien dat
             je de verkeerde kant op samenvoegt. */
          <p className="fawkes-samenvoeg__gekozen" role="status">
            <strong>{bronNaam}</strong>{" → "}
            {t("relatieSamenvoegen.gekozen", { args: [gekozen.klantnaam ?? `#${gekozen.id}`] })}
          </p>
        ) : zoek.trim().length >= 2 && rijen.length === 0 ? (
          <p className="fawkes-muted">{t("relatieSamenvoegen.geenResultaat")}</p>
        ) : (
          <ul className="fawkes-samenvoeg__lijst">
            {rijen.map((r) => (
              <li key={r.id}>
                <div>
                  <span className="fawkes-samenvoeg__naam">{r.klantnaam ?? "—"}</span>
                  <span className="fawkes-samenvoeg__sub">
                    {[r.klantNummer == null ? null : `#${r.klantNummer}`, r.woonplaats, r.emailAdres]
                      .filter(Boolean).join(" · ")}
                  </span>
                </div>
                <Button variant="secondary" onClick={() => setGekozen(r)}>
                  {t("relatieSamenvoegen.kiezen")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
